import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Store, Plus, MapPin, Users, Euro, TrendingUp, Filter, X, Loader2, Phone, Mail, History, Pencil, Sparkles, Trash2, AlertTriangle, ExternalLink, UserPlus, ShieldAlert, Copy, GitMerge, Crown, ShieldCheck, CheckCircle2, Link2, Eye, CheckSquare, Square } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { StatusBadge, TierBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { EditSpotDialog } from "@/components/EditSpotDialog";
import { ContactChips } from "@/components/ContactBlock";
import { AssocLogoStack } from "@/components/AssocLogo";
import { SavedViews } from "@/components/SavedViews";
import { SPOT_STATUS, NIVEAU_PARTENARIAT, frenchPhone, frenchDate, statusSinceShort, contactName, contactPhones, contactEmails } from "@/lib/shariti";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ALL = "__all__";

export default function BanqueSpots() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDoublons = !!user && ["admin", "cdp"].includes(user.role);
  // Enrichissement Google : accessible aux rôles pouvant ouvrir la page Prospection
  // (admin + CDP). Les admins y lancent aussi la découverte par zone.
  const canEnrich = !!user && ["admin", "cdp"].includes(user.role);
  const [params, setParams] = useSearchParams();
  const [spots, setSpots] = useState([]);
  const [territoires, setTerritoires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(params.get("status") || ALL);
  const [territoire, setTerritoire] = useState(params.get("territoire") || ALL);
  const [potentiel, setPotentiel] = useState(ALL);
  const [payant, setPayant] = useState(false);
  const [editSpot, setEditSpot] = useState(null);
  const [deleteSpotCtx, setDeleteSpotCtx] = useState(null); // spot à supprimer (confirmation)
  const [deletingSpot, setDeletingSpot] = useState(false);
  // Doublons repérés dans la banque : index spot_id -> groupe (cluster), pour proposer
  // une FUSION EN UN CLIC directement sur la ligne concernée (admin + CDP).
  const [dupIndex, setDupIndex] = useState(() => new Map());
  const [mergeCluster, setMergeCluster] = useState(null); // groupe ouvert dans le dialogue
  // Fusion MANUELLE : l'utilisateur coche lui-même des fiches qu'il sait être des
  // doublons (cas non détecté automatiquement — noms/adresses différents).
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState(() => new Set());

  const loadDuplicates = useCallback(async () => {
    if (!canDoublons) return;
    try {
      const { data } = await api.get("/spots/duplicates");
      const idx = new Map();
      (data.clusters || []).forEach((c) => (c.members || []).forEach((m) => idx.set(m.id, c)));
      setDupIndex(idx);
    } catch { /* silencieux : la détection des doublons ne doit jamais bloquer la banque */ }
  }, [canDoublons]);

  useEffect(() => { loadDuplicates(); }, [loadDuplicates]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { limit: 1000 };
      if (search) q.search = search;
      if (status !== ALL) q.status = status;
      if (territoire !== ALL) q.territoire = territoire;
      if (potentiel !== ALL) q.potentiel = potentiel;
      if (payant) q.payant = "true";
      const { data } = await api.get("/spots", { params: q });
      setSpots(data);
    } finally { setLoading(false); }
  }, [search, status, territoire, potentiel, payant]);

  // Après création d'un spot : on rafraîchit, puis si un doublon existe déjà, on
  // PROPOSE IMMÉDIATEMENT la fusion (dialogue pré-rempli) pour éviter les doublons.
  const handleSpotCreated = useCallback(async (newId) => {
    await load();
    await loadDuplicates();
    if (!newId) return;
    try {
      const { data } = await api.get(`/spots/${newId}/duplicate-group`);
      if (data.group && (data.group.members || []).length > 1) {
        toast.warning("Un spot très similaire existe déjà — fusion proposée ci-dessous.");
        setMergeCluster(data.group);
      }
    } catch { /* silencieux : la proposition de fusion ne doit jamais bloquer la création */ }
  }, [load, loadDuplicates]);

  // Suppression d'un spot de la banque. Le backend REFUSE si le spot est déjà
  // utilisé (réservation ou historique d'exploitation) afin de préserver les
  // données ; sinon il est supprimé définitivement.
  const confirmDeleteSpot = async () => {
    if (!deleteSpotCtx) return;
    setDeletingSpot(true);
    try {
      await api.delete(`/spots/${deleteSpotCtx.id}`);
      toast.success(`Spot « ${deleteSpotCtx.normalized_name} » supprimé`);
      setDeleteSpotCtx(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeletingSpot(false);
    }
  };

  useEffect(() => {
    api.get("/territoires").then((r) => setTerritoires(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const terrName = (id) => territoires.find((t) => t.id === id)?.name || "—";
  const hasFilters = status !== ALL || territoire !== ALL || potentiel !== ALL || payant || search;

  const reset = () => { setSearch(""); setStatus(ALL); setTerritoire(ALL); setPotentiel(ALL); setPayant(false); setParams({}); };

  // --- Fusion manuelle --------------------------------------------------------
  const toggleMergeSel = (id) => setMergeSel((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const exitMergeMode = () => { setMergeMode(false); setMergeSel(new Set()); };
  const openManualMerge = () => {
    const chosen = spots.filter((s) => mergeSel.has(s.id));
    if (chosen.length < 2) { toast.info("Sélectionnez au moins 2 fiches à fusionner."); return; }
    const members = chosen.map((s) => ({
      id: s.id,
      nom_donne: s.nom_donne,
      normalized_name: s.normalized_name,
      enseigne: s.enseigne,
      address_full: s.address_full,
      rue: s.rue,
      ville: s.ville,
      telephone: (contactPhones ? contactPhones(s.contact_principal)?.[0] : null) || s.contact_principal?.telephone || null,
      exploitations: s.exploitations_count ?? 0,
      has_gps: !!(s.gps_lat != null && s.gps_lng != null),
    }));
    // Référence suggérée : la fiche avec une adresse complète, sinon la première cochée.
    const withAddr = members.find((m) => m.address_full);
    setMergeCluster({ suggested_canonical_id: (withAddr || members[0]).id, members, manual: true });
  };

  return (
    <>
      <PageHeader
        title="Banque de spots"
        subtitle="Recherchez, filtrez et ouvrez la fiche complète de chaque emplacement."
        action={
          <div className="flex items-center gap-2">
            {canEnrich && (
              <Button variant="outline"
                onClick={() => {
                  const sp = new URLSearchParams({ tab: "google" });
                  if (user?.role === "admin") sp.set("discover", "1");
                  if (search.trim()) sp.set("q", search.trim());
                  navigate(`/prospection?${sp.toString()}`);
                }}
                className="border-[rgba(238,114,46,0.4)] bg-[#FFF3E9] text-[#8A3A10] hover:bg-[#FFE9D8]"
                data-testid="banque-enrich-google-btn"
                title="Enrichir la banque via Google Places (par région / département / ville)">
                <Sparkles className="mr-1.5 h-4 w-4" /> Enrichir via Google
              </Button>
            )}
            {canDoublons && (
              <Button variant="outline" onClick={() => navigate(`/doublons-spots${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ""}`)}
                className="border-[var(--border-soft)] bg-white" data-testid="banque-doublons-btn"
                title="Détecter et fusionner les fiches en double (le nom recherché est repris)">
                <Copy className="mr-1.5 h-4 w-4" /> Gérer les doublons
              </Button>
            )}
            {canDoublons && (
              <Button variant="outline" onClick={() => (mergeMode ? exitMergeMode() : setMergeMode(true))}
                className={mergeMode ? "border-transparent text-white" : "border-[var(--border-soft)] bg-white"}
                style={mergeMode ? { background: "var(--shariti-orange)" } : undefined}
                data-testid="banque-manual-merge-toggle"
                title="Sélectionner vous-même des fiches à fusionner (doublons non repérés automatiquement)">
                {mergeMode ? <><X className="mr-1.5 h-4 w-4" /> Terminer</> : <><GitMerge className="mr-1.5 h-4 w-4" /> Fusionner manuellement</>}
              </Button>
            )}
            <NewSpotDialog territoires={territoires} onCreated={handleSpotCreated} />
          </div>
        }
      >
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un spot, une ville, un dépt…"
              className="h-10 bg-white pl-9" data-testid="spot-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-10 w-[180px] bg-white" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les statuts</SelectItem>
              {Object.entries(SPOT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={territoire} onValueChange={setTerritoire}>
            <SelectTrigger className="h-10 w-[170px] bg-white" data-testid="filter-territoire"><SelectValue placeholder="Territoire" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les territoires</SelectItem>
              {territoires.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={potentiel} onValueChange={setPotentiel}>
            <SelectTrigger className="h-10 w-[140px] bg-white" data-testid="filter-potentiel"><SelectValue placeholder="Potentiel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tout potentiel</SelectItem>
              <SelectItem value="A">Potentiel A</SelectItem>
              <SelectItem value="B">Potentiel B</SelectItem>
              <SelectItem value="C">Potentiel C</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex h-10 items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-3 text-xs font-semibold">
            <Switch checked={payant} onCheckedChange={setPayant} data-testid="filter-payant" /> Payants
          </label>
          {hasFilters && (
            <Button variant="ghost" onClick={reset} className="h-10" data-testid="filter-reset"><X className="mr-1 h-4 w-4" /> Réinitialiser</Button>
          )}
          <SavedViews page="banque"
            current={{ search, status, territoire, potentiel, payant }}
            onApply={(p) => {
              setSearch(p.search || "");
              setStatus(p.status || ALL);
              setTerritoire(p.territoire || ALL);
              setPotentiel(p.potentiel || ALL);
              setPayant(!!p.payant);
            }}
            describe={(p) => {
              const parts = [];
              if (p.status && p.status !== ALL) parts.push(SPOT_STATUS[p.status]?.label || p.status);
              if (p.territoire && p.territoire !== ALL) parts.push(terrName(p.territoire));
              if (p.potentiel && p.potentiel !== ALL) parts.push(`Pot. ${p.potentiel}`);
              if (p.payant) parts.push("Payants");
              if (p.search) parts.push(`« ${p.search} »`);
              return parts.join(" · ") || "Tous";
            }}
            buttonClassName="h-10 bg-white" />
        </div>
      </PageHeader>

      <PageBody>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-muted-ink" data-testid="spot-count">
            {loading ? "Chargement…" : `${spots.length} spot${spots.length > 1 ? "s" : ""}`}
          </span>
        </div>

        {mergeMode && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-[var(--shariti-orange)] bg-[#FFF7F1] px-3 py-2.5 text-[12px] text-[#8A3A10]" data-testid="manual-merge-hint">
            <GitMerge className="mt-0.5 h-4 w-4 shrink-0" />
            <span><b>Mode fusion manuelle.</b> Cochez les fiches que vous savez être le même magasin (au moins 2), puis cliquez sur « Fusionner » en bas. Vous choisirez ensuite la fiche à conserver ; rien n'est écrasé ni supprimé.</span>
          </div>
        )}

        {loading ? (
          <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-white" />)}</div>
        ) : spots.length === 0 ? (
          <EmptyState icon={Store} title="Aucun spot ne correspond"
            message="Essayez d'élargir la recherche ou de réinitialiser les filtres."
            actionLabel={hasFilters ? "Réinitialiser les filtres" : undefined} onAction={reset} testId="spots-empty" />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
            {/* Desktop table */}
            <div className="hidden @4xl/page:block">
              <table className="w-full table-fixed text-[12px]">
                <thead>
                  <tr className="border-b border-[var(--border-soft)] text-left text-[10px] uppercase tracking-wide text-muted-ink">
                    <th className="w-[22%] px-2 py-2 font-semibold">Spot</th>
                    <th className="w-[18%] px-2 py-2 font-semibold">Adresse</th>
                    <th className="w-[11%] px-2 py-2 font-semibold">Statut</th>
                    <th className="w-[12%] px-2 py-2 font-semibold">Territoire</th>
                    <th className="w-[9%] px-2 py-2 font-semibold">Flux / Pot.</th>
                    <th className="w-[7%] px-2 py-2 font-semibold">Conv.</th>
                    <th className="w-[16%] px-2 py-2 font-semibold">Contact</th>
                    <th className="w-[5%] px-2 py-2 text-right font-semibold">Act.</th>
                  </tr>
                </thead>
                <tbody>
                  {spots.map((s, i) => (
                    <motion.tr key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.015, 0.3) }}
                      onClick={() => (mergeMode ? toggleMergeSel(s.id) : navigate(`/spots/${s.id}`))} data-testid={`spot-row-${s.id}`}
                      className={`cursor-pointer border-b border-[var(--border-soft)] last:border-0 hover:bg-[rgba(238,114,46,0.04)] ${mergeMode && mergeSel.has(s.id) ? "bg-[rgba(238,114,46,0.12)]" : ""}`}>
                      <td className="px-2 py-2 align-top">
                        <div className="flex items-start gap-1.5">
                          {mergeMode && (
                            <span className="mt-0.5 shrink-0" data-testid={`spot-merge-check-${s.id}`}>
                              {mergeSel.has(s.id) ? <CheckSquare className="h-4 w-4 text-[var(--shariti-orange)]" /> : <Square className="h-4 w-4 text-muted-ink" />}
                            </span>
                          )}
                          <span className="break-words font-bold leading-tight text-ink text-[11px]" title={s.normalized_name}>{s.normalized_name}</span>
                          {s.is_historical && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[rgba(99,102,241,0.12)] px-1 py-0.5 text-[9px] font-bold uppercase text-[#4338CA]" data-testid={`spot-hist-${s.id}`}><History className="h-2.5 w-2.5" /></span>}
                          {s.source_import === "google_suggestion" && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[rgba(47,111,237,0.12)] px-1 py-0.5 text-[9px] font-bold uppercase text-[#1E4FB8]" data-testid={`spot-google-${s.id}`}><Sparkles className="h-2.5 w-2.5" /></span>}
                          {dupIndex.has(s.id) && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[rgba(238,114,46,0.14)] px-1 py-0.5 text-[9px] font-bold uppercase text-[#8A3A10]" data-testid={`spot-dup-badge-${s.id}`} title="Doublon détecté"><Copy className="h-2.5 w-2.5" /> Doublon</span>}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[9px] text-muted-ink">
                          <span>{s.id}</span>
                          {s.is_payant && <span className="inline-flex items-center gap-0.5 text-[#8A3A10]"><Euro className="h-2.5 w-2.5" /> Payant</span>}
                          {s.niveau_partenariat !== "ponctuel" && <span className="text-[var(--shariti-orange)]">{NIVEAU_PARTENARIAT[s.niveau_partenariat]}</span>}
                        </div>
                        {(s.created_by_name || s.created_at) && (
                          <div className="mt-0.5 flex items-center gap-1 text-[9px] leading-tight text-muted-ink/80" data-testid={`spot-creator-${s.id}`} title={`Créé${s.created_by_name ? ` par ${s.created_by_name}` : ""}${s.created_at ? ` le ${frenchDate(s.created_at)}` : ""}`}>
                            <UserPlus className="h-2.5 w-2.5 shrink-0" />
                            <span className="truncate">Créé{s.created_by_name ? ` par ${s.created_by_name}` : ""}{s.created_at ? ` · ${frenchDate(s.created_at)}` : ""}</span>
                          </div>
                        )}
                        {s.assos_history?.items?.length > 0 && (
                          <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                            <AssocLogoStack items={s.assos_history.items} size="sm" max={4} testId={`spot-assos-${s.id}`} />
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        {s.address_full
                          ? <span className="flex items-start gap-1 text-[10px] leading-snug text-muted-ink" data-testid={`spot-address-${s.id}`} title={s.address_full}><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-words">{s.address_full}</span></span>
                          : <span className="text-[10px] text-muted-ink/60">—</span>}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <StatusBadge status={s.status} size="xs" />
                        {statusSinceShort(s) && <div className="mt-1 text-[9px] leading-tight text-muted-ink" data-testid={`spot-status-since-${s.id}`}>{statusSinceShort(s)}</div>}
                      </td>
                      <td className="px-2 py-2 align-top"><span className="flex items-start gap-1 text-[10px] leading-tight"><MapPin className="mt-0.5 h-3 w-3 shrink-0 text-muted-ink" /><span className="break-words">{terrName(s.territoire_id)}{s.dept ? ` (${s.dept})` : ""}</span></span></td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-semibold">{s.flux_effective ? `${s.flux_effective}/j` : "—"}</span>
                          <TierBadge tier={s.potential_tier} />
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top text-[11px] font-semibold">{s.avg_conversion != null ? `${(s.avg_conversion * 100).toFixed(1)}%` : "—"}</td>
                      <td className="px-2 py-2 align-top"><ContactCell spot={s} compact /></td>
                      <td className="px-2 py-2 text-right align-top">
                        <div className="flex items-center justify-end gap-0.5">
                          {dupIndex.has(s.id) && (
                            <Button variant="ghost" size="sm"
                              onClick={(e) => { e.stopPropagation(); setMergeCluster(dupIndex.get(s.id)); }}
                              data-testid={`spot-dup-merge-${s.id}`}
                              className="h-7 gap-1 px-1.5 text-[10px] font-bold text-[#8A3A10] bg-[rgba(238,114,46,0.12)] hover:bg-[rgba(238,114,46,0.22)]"
                              title="Doublon détecté — fusionner en un clic">
                              <GitMerge className="h-3.5 w-3.5" /> Fusionner
                            </Button>
                          )}
                          <Button variant="ghost" size="sm"
                            onClick={(e) => { e.stopPropagation(); setEditSpot(s); }}
                            data-testid={`spot-edit-${s.id}`}
                            className="h-7 w-7 p-0 text-muted-ink hover:bg-[rgba(238,114,46,0.1)] hover:text-[var(--shariti-orange)]"
                            title="Modifier le spot" aria-label="Modifier le spot">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={(e) => { e.stopPropagation(); setDeleteSpotCtx(s); }}
                            data-testid={`spot-delete-${s.id}`}
                            className="h-7 w-7 p-0 text-muted-ink hover:bg-[rgba(214,69,69,0.1)] hover:text-[#D64545]"
                            title="Supprimer le spot" aria-label="Supprimer le spot">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="divide-y divide-[var(--border-soft)] @4xl/page:hidden">
              {spots.map((s) => (
                <div key={s.id} data-testid={`spot-card-${s.id}`} className={`p-4 ${mergeMode && mergeSel.has(s.id) ? "bg-[rgba(238,114,46,0.12)]" : ""}`}>
                  <button onClick={() => (mergeMode ? toggleMergeSel(s.id) : navigate(`/spots/${s.id}`))} className="block w-full text-left">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-start gap-1.5">
                          {mergeMode && (mergeSel.has(s.id)
                            ? <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" data-testid={`spot-merge-check-m-${s.id}`} />
                            : <Square className="mt-0.5 h-4 w-4 shrink-0 text-muted-ink" data-testid={`spot-merge-check-m-${s.id}`} />)}
                          <span className="break-words font-bold leading-tight">{s.normalized_name}</span>
                          {s.is_historical && <History className="mt-0.5 h-3 w-3 shrink-0 text-[#4338CA]" />}
                          {s.source_import === "google_suggestion" && <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-[#1E4FB8]" data-testid={`spot-google-m-${s.id}`} />}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-ink">{terrName(s.territoire_id)} {s.dept ? `(${s.dept})` : ""} • {s.flux_effective ? `${s.flux_effective}/j` : "flux ?"}</div>
                        {s.address_full && <div className="mt-0.5 flex items-start gap-1 text-[11px] text-muted-ink" data-testid={`spot-address-m-${s.id}`}><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-words">{s.address_full}</span></div>}
                        {(s.created_by_name || s.created_at) && (
                          <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-ink/80" data-testid={`spot-creator-m-${s.id}`}>
                            <UserPlus className="h-3 w-3 shrink-0" />
                            <span className="truncate">Créé{s.created_by_name ? ` par ${s.created_by_name}` : ""}{s.created_at ? ` · ${frenchDate(s.created_at)}` : ""}</span>
                          </div>
                        )}
                      </div>
                      <TierBadge tier={s.potential_tier} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <StatusBadge status={s.status} />
                      {statusSinceShort(s) && <span className="text-[10px] text-muted-ink">{statusSinceShort(s)}</span>}
                    </div>
                    {s.assos_history?.items?.length > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-muted-ink">Assos :</span>
                        <AssocLogoStack items={s.assos_history.items} size="sm" max={5} testId={`spot-assos-m-${s.id}`} />
                      </div>
                    )}
                  </button>
                  <div className="mt-2 flex items-end justify-between gap-2">
                    <ContactCell spot={s} />
                    <div className="flex shrink-0 items-center gap-1">
                      {dupIndex.has(s.id) && (
                        <Button variant="ghost" size="sm"
                          onClick={() => setMergeCluster(dupIndex.get(s.id))}
                          data-testid={`spot-dup-merge-m-${s.id}`}
                          className="gap-1 text-[#8A3A10] bg-[rgba(238,114,46,0.12)] hover:bg-[rgba(238,114,46,0.22)]"
                          title="Doublon détecté — fusionner">
                          <GitMerge className="h-3.5 w-3.5" /> Doublon
                        </Button>
                      )}
                      <Button variant="ghost" size="sm"
                        onClick={() => setEditSpot(s)}
                        data-testid={`spot-edit-m-${s.id}`}
                        className="text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.1)]">
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Modifier
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => setDeleteSpotCtx(s)}
                        data-testid={`spot-delete-m-${s.id}`}
                        className="h-8 w-8 p-0 text-[#D64545] hover:bg-[rgba(214,69,69,0.1)]"
                        title="Supprimer le spot" aria-label="Supprimer le spot">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {mergeMode && <div className="h-20" aria-hidden />}
      </PageBody>

      {/* Barre d'action fixe du mode fusion manuelle */}
      {mergeMode && (
        <div className="fixed inset-x-0 bottom-0 z-[200] border-t border-[var(--border-soft)] bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur" data-testid="manual-merge-bar">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <GitMerge className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
              <span className="font-semibold text-ink" data-testid="manual-merge-count">
                {mergeSel.size} fiche{mergeSel.size > 1 ? "s" : ""} sélectionnée{mergeSel.size > 1 ? "s" : ""}
              </span>
              <span className="hidden truncate text-muted-ink sm:inline">— cochez les doublons puis fusionnez</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="ghost" onClick={exitMergeMode} data-testid="manual-merge-cancel">Annuler</Button>
              <Button onClick={openManualMerge} disabled={mergeSel.size < 2}
                className="text-white disabled:opacity-50" style={{ background: "var(--shariti-orange)" }}
                data-testid="manual-merge-confirm">
                <GitMerge className="mr-1.5 h-4 w-4" /> Fusionner ({mergeSel.size})
              </Button>
            </div>
          </div>
        </div>
      )}

      <EditSpotDialog
        open={!!editSpot}
        onClose={() => setEditSpot(null)}
        spot={editSpot}
        onSaved={() => { setEditSpot(null); load(); }}
      />

      {/* Fusion EN UN CLIC d'un doublon repéré depuis la banque (admin + CDP) */}
      <InlineDuplicateMerge
        cluster={mergeCluster}
        onClose={() => setMergeCluster(null)}
        onMerged={() => { setMergeCluster(null); exitMergeMode(); load(); loadDuplicates(); }}
      />

      {/* Suppression d'un spot (confirmation) */}
      <AlertDialog open={!!deleteSpotCtx} onOpenChange={(o) => { if (!o && !deletingSpot) setDeleteSpotCtx(null); }}>
        <AlertDialogContent data-testid="delete-spot-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le spot « {deleteSpotCtx?.normalized_name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est <strong>définitive</strong>. La suppression est <strong>refusée</strong> si le spot
              est utilisé dans une réservation ou possède un historique d'exploitation (afin de préserver les données).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-spot-cancel" disabled={deletingSpot}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-spot-confirm"
              disabled={deletingSpot}
              onClick={(e) => { e.preventDefault(); confirmDeleteSpot(); }}
              className="bg-[#D64545] text-white hover:bg-[#c23b3b]">
              {deletingSpot ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ContactCell({ spot, compact = false }) {
  const primary = spot?.contact_principal;
  const extra = (spot?.contacts_secondaires || []).filter(
    (c) => contactName(c) || contactPhones(c).length || contactEmails(c).length
  ).length;
  const hasPrimary = primary && (contactName(primary) || contactPhones(primary).length || contactEmails(primary).length);
  if (!hasPrimary && extra === 0) return <span className="text-xs text-muted-ink">—</span>;
  return (
    <div className="w-full min-w-0">
      {hasPrimary ? <ContactChips contact={primary} stack compact={compact} /> : <span className="text-xs text-muted-ink">Aucun contact principal</span>}
      {extra > 0 && (
        <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-semibold text-muted-ink">
          <Users className="h-3 w-3" /> +{extra} autre{extra > 1 ? "s" : ""} contact{extra > 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

function NewSpotDialog({ territoires, onCreated }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ enseigne: "", ville: "", arrondissement: "", rue: "", nom_donne: "", dept: "", territoire_id: "", type: "supermarche", consignes: "", flux: "", status: "jamais_appele" });
  const [similar, setSimilar] = useState([]);        // spots ressemblants détectés EN DIRECT
  const [checkingSim, setCheckingSim] = useState(false);
  const [dupCtx, setDupCtx] = useState(null);        // réponse 409 structurée -> dialogue de confirmation
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const preview = [f.enseigne, `${f.ville}${f.arrondissement ? " " + f.arrondissement : ""}`, f.rue, f.nom_donne].filter(Boolean).join(" ").toUpperCase();

  // Détection EN DIRECT des doublons : dès que l'enseigne + la ville sont saisies,
  // on cherche les spots ressemblants (recherche fluide) pour inviter à reprendre
  // un spot existant plutôt qu'à créer un doublon.
  useEffect(() => {
    if (!open || !f.enseigne.trim() || !f.ville.trim()) { setSimilar([]); return; }
    let cancelled = false;
    setCheckingSim(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/spots/similar", {
          params: { enseigne: f.enseigne, ville: f.ville, rue: f.rue || undefined, nom_donne: f.nom_donne || undefined, limit: 6 },
        });
        if (!cancelled) setSimilar(data.results || []);
      } catch { if (!cancelled) setSimilar([]); }
      finally { if (!cancelled) setCheckingSim(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, f.enseigne, f.ville, f.rue, f.nom_donne]);

  const resetForm = () => setF({ enseigne: "", ville: "", arrondissement: "", rue: "", nom_donne: "", dept: "", territoire_id: "", type: "supermarche", consignes: "", flux: "", status: "jamais_appele" });

  const submit = async (force = false) => {
    if (!f.enseigne || !f.ville) { toast.error("Enseigne et ville obligatoires"); return; }
    setSaving(true);
    try {
      const payload = { ...f, force, flux: f.flux ? { mode: "global", global_value: Number(f.flux), source: "estime_cdp" } : {} };
      const { data } = await api.post("/spots", payload);
      toast.success(`Spot créé : ${data.normalized_name}`);
      setDupCtx(null); setSimilar([]); setOpen(false); onCreated?.(data.id);
      resetForm();
    } catch (err) {
      if (err?.response?.status === 409) {
        // Doublon détecté côté serveur : on ouvre un dialogue clair (reprendre / créer quand même).
        setDupCtx(err.response.data || { detail: "Doublon potentiel détecté." });
      } else toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const openExisting = (id) => {
    setDupCtx(null); setSimilar([]); setOpen(false); resetForm();
    navigate(`/spots/${id}`);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSimilar([]); setDupCtx(null); } }}>
        <DialogTrigger asChild>
          <Button className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="new-spot-btn"><Plus className="mr-2 h-4 w-4" /> Nouveau spot</Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nouveau spot</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Enseigne *</Label><Input value={f.enseigne} onChange={set("enseigne")} placeholder="Monoprix" data-testid="ns-enseigne" /></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={f.type} onValueChange={set("type")}>
                  <SelectTrigger data-testid="ns-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="supermarche">Supermarché</SelectItem><SelectItem value="galerie">Galerie</SelectItem><SelectItem value="autre">Autre</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Ville *</Label><Input value={f.ville} onChange={set("ville")} placeholder="Paris" data-testid="ns-ville" /></div>
              <div><Label className="text-xs">Arrondissement</Label><Input value={f.arrondissement} onChange={set("arrondissement")} placeholder="14" data-testid="ns-arr" /></div>
              <div><Label className="text-xs">Rue / Bd / Place</Label><Input value={f.rue} onChange={set("rue")} placeholder="Général Leclerc" data-testid="ns-rue" /></div>
              <div><Label className="text-xs">Nom donné</Label><Input value={f.nom_donne} onChange={set("nom_donne")} placeholder="Daguerre" data-testid="ns-nom" /></div>
              <div><Label className="text-xs">Département</Label><Input value={f.dept} onChange={set("dept")} placeholder="75" data-testid="ns-dept" /></div>
              <div><Label className="text-xs">Flux clients/jour</Label><Input type="number" value={f.flux} onChange={set("flux")} placeholder="800" data-testid="ns-flux" /></div>
            </div>
            <div><Label className="text-xs">Territoire</Label>
              <Select value={f.territoire_id} onValueChange={set("territoire_id")}>
                <SelectTrigger data-testid="ns-territoire"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{territoires.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-muted-ink">Laissez vide : le territoire est déduit automatiquement du département.</p>
            </div>
            <div><Label className="text-xs">Consignes spécifiques</Label><Textarea value={f.consignes} onChange={set("consignes")} placeholder="ex : rester devant le magasin" data-testid="ns-consignes" /></div>

            {/* Alerte anti-doublon EN DIRECT : spots ressemblants déjà en banque */}
            {similar.length > 0 && (
              <div className="rounded-xl border border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.07)] p-3" data-testid="ns-similar-warning">
                <div className="flex items-center gap-1.5 text-[12px] font-bold text-[#B5470F]">
                  <AlertTriangle className="h-4 w-4" />
                  {similar.length} spot{similar.length > 1 ? "s" : ""} ressemblant{similar.length > 1 ? "s" : ""} déjà en banque
                </div>
                <p className="mt-0.5 text-[11px] text-muted-ink">Reprenez plutôt un spot existant pour éviter un doublon.</p>
                <div className="mt-2 space-y-1">
                  {similar.map((s) => (
                    <button key={s.id} type="button" onClick={() => openExisting(s.id)} data-testid={`ns-similar-${s.id}`}
                      className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5 text-left text-[11px] shadow-[var(--shadow-sm)] transition-colors hover:bg-[rgba(238,114,46,0.08)]">
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-ink">{s.normalized_name}</span>
                        {s.address_full && <span className="block truncate text-[10px] text-muted-ink">{s.address_full}</span>}
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 font-semibold text-[var(--shariti-orange)]"><ExternalLink className="h-3 w-3" /> Ouvrir</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {checkingSim && similar.length === 0 && f.enseigne && f.ville && (
              <p className="flex items-center gap-1 text-[10px] text-muted-ink" data-testid="ns-similar-checking"><Loader2 className="h-3 w-3 animate-spin" /> Vérification des doublons…</p>
            )}

            {preview && <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-xs"><span className="text-muted-ink">Nom normalisé : </span><span className="font-bold">{preview}</span></div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => submit(false)} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="ns-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer le spot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de confirmation quand le serveur détecte un doublon (409 structuré) */}
      <AlertDialog open={!!dupCtx} onOpenChange={(o) => { if (!o) setDupCtx(null); }}>
        <AlertDialogContent data-testid="ns-duplicate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#B5470F]">
              <AlertTriangle className="h-5 w-5" /> Doublon potentiel détecté
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p>{dupCtx?.detail}</p>
                {dupCtx?.existing && (
                  <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5">
                    <div className="font-semibold text-ink">{dupCtx.existing.normalized_name}</div>
                    {dupCtx.existing.address_full && <div className="mt-0.5 flex items-start gap-1 text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{dupCtx.existing.address_full}</div>}
                    <div className="mt-0.5 text-[11px] text-muted-ink">{dupCtx.existing.id}{dupCtx.match_label ? ` · correspondance : ${dupCtx.match_label}` : ""}</div>
                  </div>
                )}
                {Array.isArray(dupCtx?.ambiguous) && dupCtx.ambiguous.length > 0 && (
                  <div className="space-y-1">
                    {dupCtx.ambiguous.map((m) => (
                      <button key={m.id} type="button" onClick={() => openExisting(m.id)} data-testid={`ns-dup-open-${m.id}`}
                        className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-left text-[11px] hover:bg-[rgba(238,114,46,0.08)]">
                        <span className="truncate font-semibold">{m.nom} <span className="text-muted-ink">({m.id}{m.distance_m != null ? `, ${m.distance_m} m` : ""})</span></span>
                        <span className="inline-flex shrink-0 items-center gap-1 font-semibold text-[var(--shariti-orange)]"><ExternalLink className="h-3 w-3" /> Ouvrir</span>
                      </button>
                    ))}
                  </div>
                )}
                {!(dupCtx?.can_force ?? isAdmin) && (
                  <div className="flex items-start gap-2 rounded-lg border border-[rgba(214,69,69,0.3)] bg-[rgba(214,69,69,0.07)] p-2.5 text-[12px] text-[#9B2226]" data-testid="ns-dup-admin-only">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span><b>Création bloquée.</b> Ouvrez le spot existant ci-dessus. Seul un <b>administrateur</b> peut forcer la création en cas de vrai doublon distinct.</span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {dupCtx?.existing?.id && (
              <Button variant="outline" onClick={() => openExisting(dupCtx.existing.id)} data-testid="ns-dup-open-existing"
                className="border-[var(--shariti-orange)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)]">
                <ExternalLink className="mr-1.5 h-4 w-4" /> Ouvrir le spot existant
              </Button>
            )}
            <div className="flex gap-2">
              <AlertDialogCancel data-testid="ns-dup-cancel">Annuler</AlertDialogCancel>
              {(dupCtx?.can_force ?? isAdmin) && (
                <AlertDialogAction data-testid="ns-dup-force"
                  onClick={(e) => { e.preventDefault(); setDupCtx(null); submit(true); }}
                  className="bg-[#B5470F] text-white hover:bg-[#993c0c]">
                  Créer quand même
                </AlertDialogAction>
              )}
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


// ---------------------------------------------------------------------------
// Fusion EN UN CLIC d'un doublon, directement depuis la Banque de spots.
// Réutilise les endpoints /spots/merge/preview (simulation) et /spots/merge.
// La référence est conservée, ses champs vides complétés, les contacts annexés —
// jamais d'écrasement, jamais de suppression (les doublons passent en archive).
// ---------------------------------------------------------------------------
function InlineDuplicateMerge({ cluster, onClose, onMerged }) {
  const open = !!cluster;
  const [canonicalId, setCanonicalId] = useState(null);
  const [included, setIncluded] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  // (Ré)initialise à l'ouverture / au changement de groupe.
  useEffect(() => {
    if (!cluster) return;
    setCanonicalId(cluster.suggested_canonical_id);
    setIncluded(new Set(
      (cluster.members || [])
        .filter((m) => m.id !== cluster.suggested_canonical_id)
        .map((m) => m.id)
    ));
    setPreview(null);
  }, [cluster]);

  const dupIds = useMemo(
    () => [...included].filter((id) => id !== canonicalId),
    [included, canonicalId]
  );

  // Aperçu automatique (simulation) à chaque changement de référence / sélection.
  useEffect(() => {
    if (!open || !canonicalId || dupIds.length === 0) { setPreview(null); return; }
    let alive = true;
    setPreviewBusy(true);
    api.post("/spots/merge/preview", { canonical_id: canonicalId, duplicate_ids: dupIds })
      .then(({ data }) => { if (alive) setPreview(data); })
      .catch(() => { if (alive) setPreview(null); })
      .finally(() => { if (alive) setPreviewBusy(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canonicalId, dupIds.join(",")]);

  const pickCanonical = (id) => {
    setCanonicalId(id);
    setIncluded((prev) => {
      const next = new Set(prev);
      next.delete(id);
      (cluster.members || []).forEach((m) => { if (m.id !== id) next.add(m.id); });
      return next;
    });
  };
  const toggleInclude = (id) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const doMerge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/spots/merge", { canonical_id: canonicalId, duplicate_ids: dupIds });
      toast.success(`Fusion effectuée — ${data.merged?.length ?? dupIds.length} doublon(s) rattaché(s) à la référence`);
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fusion impossible");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inline-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-[var(--shariti-orange)]" /> Fusionner ce doublon
          </DialogTitle>
          <DialogDescription>
            Choisissez la fiche à conserver (référence). Ses champs vides seront complétés et les contacts du
            doublon ajoutés — <b>rien n'est écrasé, rien n'est supprimé</b>. Rien n'est appliqué tant que vous
            n'avez pas cliqué sur « Fusionner ».
          </DialogDescription>
        </DialogHeader>

        {cluster && (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {(cluster.members || []).map((m) => {
                const isRef = m.id === canonicalId;
                const isInc = included.has(m.id);
                return (
                  <div key={m.id}
                    className={`rounded-xl border p-2.5 text-[12px] ${isRef ? "border-[var(--shariti-orange)] bg-[#FFF7F1]" : "border-[var(--border-soft)] bg-white"}`}
                    data-testid={`inline-member-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="break-words font-bold text-ink">{m.nom_donne || m.normalized_name || m.enseigne || m.id}</div>
                        <div className="font-mono text-[10px] text-muted-ink">{m.id}</div>
                      </div>
                      {isRef ? (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-[var(--shariti-orange)] px-2 py-0.5 text-[9px] font-bold uppercase text-white">
                          <Crown className="mr-0.5 h-3 w-3" /> Référence
                        </span>
                      ) : (
                        <label className="flex shrink-0 cursor-pointer items-center gap-1 text-[10px] text-muted-ink">
                          <input type="checkbox" checked={isInc} onChange={() => toggleInclude(m.id)} data-testid={`inline-include-${m.id}`} />
                          Fusionner
                        </label>
                      )}
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-[11px] text-muted-ink">
                      <div className="flex items-start gap-1"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-words">{m.address_full || [m.rue, m.ville].filter(Boolean).join(", ") || "—"}</span></div>
                      <div className="flex items-center gap-1"><Phone className="h-3 w-3 shrink-0" />{m.telephone || "—"}</div>
                      <div className="flex items-center gap-1"><Users className="h-3 w-3 shrink-0" />{m.exploitations ?? 0} exploitation(s){m.has_gps ? " · GPS ✓" : " · GPS manquant"}</div>
                    </div>
                    {!isRef && (
                      <Button variant="ghost" size="sm" onClick={() => pickCanonical(m.id)}
                        className="mt-1.5 h-6 w-full text-[10px] text-[var(--shariti-orange)] hover:bg-[#FFF3E9]"
                        data-testid={`inline-set-canon-${m.id}`}>
                        <Crown className="mr-1 h-3 w-3" /> Choisir comme référence
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-[12px]" data-testid="inline-merge-preview">
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-ink">
                <Eye className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Ce qui sera fait (aucune perte d'info)
              </div>
              {previewBusy ? (
                <div className="flex items-center gap-2 text-muted-ink"><Loader2 className="h-4 w-4 animate-spin" /> Calcul de l'aperçu…</div>
              ) : dupIds.length === 0 ? (
                <div className="text-muted-ink">Sélectionnez au moins un doublon à fusionner.</div>
              ) : preview ? (
                <div className="space-y-1">
                  {((preview.fields_to_fill?.length || 0) + (preview.contacts_to_add?.length || 0)) === 0 ? (
                    <div className="text-muted-ink">La référence est déjà complète — rien à compléter (rien ne sera écrasé).</div>
                  ) : (
                    <>
                      {(preview.fields_to_fill || []).map((f, i) => (
                        <div key={`f${i}`} className="flex items-center gap-1.5">
                          <CheckCircle2 className="h-3 w-3 shrink-0 text-[#1F9D55]" />
                          <b className="text-ink">{f.field}</b>
                          <span className="truncate text-muted-ink">= {String(f.value)}</span>
                        </div>
                      ))}
                      {(preview.contacts_to_add || []).map((c, i) => (
                        <div key={`c${i}`} className="flex items-center gap-1.5">
                          <CheckCircle2 className="h-3 w-3 shrink-0 text-[#1F9D55]" />
                          <b className="text-ink">contact ajouté</b>
                          <span className="break-words text-muted-ink">{[c.nom, c.telephone, c.email].filter(Boolean).join(" · ") || "—"}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {preview.references_total > 0 && (
                    <div className="flex items-center gap-1.5 pt-1 text-muted-ink">
                      <Link2 className="h-3 w-3 shrink-0" /> {preview.references_total} donnée(s) rattachée(s) (planning, appels, pointages…) rebranchée(s) vers la référence
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-muted-ink">—</div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="inline-merge-cancel">Annuler</Button>
          <Button onClick={doMerge} disabled={busy || dupIds.length === 0}
            className="text-white disabled:opacity-50" style={{ background: "var(--shariti-orange)" }}
            data-testid="inline-merge-confirm">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <GitMerge className="mr-1.5 h-4 w-4" />}
            Fusionner ({dupIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
